// Shared step-list machinery for the client-facing wizards that run on the
// checkup shell (components/checkup/CheckupView.vue + mixins/checkup-page.js):
// /checkup/* from #598 and /renewal/* from #656.
//
// Each wizard owns nothing but its STEPS array — order, paths, labels — and
// everything else is derived here, so adding a third wizard is one config
// file plus one line in data/wizards.js.
//
// `stepForRoute` matches on the LAST path segment only, so two wizards may
// reuse a step id (both have `sign` and `done`). Picking the right wizard for
// a route is data/wizards.js's job, by base path; this helper is only ever
// called on a wizard that already owns the route.

export function makeWizard ({ id, basePath, steps }) {
    return {
        id,
        basePath,
        // Every step this wizard defines, including ones a given client never
        // sees. Route resolution and jump targets read this: "which page is
        // /checkup/plans" has one answer regardless of who is asking.
        steps,
        // The steps THIS client actually walks. A step may carry a
        // `when(getters)` predicate — /checkup/plans is selfserve-only, the
        // same way the classic survey nav gated it (store/index.js's
        // surveyRoutes). Steps without one are always included, so a wizard
        // that uses no predicates (i.e. /renewal) is unaffected.
        stepsFor: getters => steps.filter(s => !s.when || s.when(getters)),
        // Of those, the ones the chrome (stepper, progress bar, meta strip)
        // counts and renders. A step opts out with `chrome: false` —
        // /renewal's landing and confirmation screens sit outside the numbered
        // run, matching the designs in #656 where the first numbered dot is
        // the contact step. Checkup sets this nowhere, so its chrome spans
        // every step its client is shown.
        visibleStepsFor: getters => steps.filter(s => (!s.when || s.when(getters)) && s.chrome !== false),
        stepIndex: stepId => steps.findIndex(s => s.id === stepId),
        stepForRoute: (route) => {
            const slug = (route?.path || '').replace(/\/+$/, '').split('/').pop()
            return steps.find(s => s.id === slug) || null
        },
    }
}
